import { z } from 'zod';
/**
 * ServiceM8 domain schemas.
 *
 * Live-confirmed against Leading Edge's tenant. Real shapes:
 *   - `/job/<uuid>.json`   → Sm8JobSchema
 *   - `/company/<uuid>.json` → Sm8CompanySchema
 *
 * The "Job Form" custom-field path (`/jobformfield.json`) is NOT authorised on
 * this tenant — the API key returns 403 — and the form has 0 active responses
 * ever, so we deliberately don't model it. SM8's role here is cover-sheet
 * boilerplate only: address, scheduled date, billing company, category. Hardware
 * identifiers come from Dropbox photos / vision, not SM8.
 *
 * Schemas use `.passthrough()` so unknown fields don't fail validation.
 */
/** Opaque ServiceM8 UUID. */
/**
 * Bounds of the region of a ServiceM8 job description that this tool owns.
 *
 * Shared because both sides need them: the backend splices the block between
 * them on a push, and the dashboard reads them to tell the operator how many
 * work orders carry notes somebody else wrote. Changing either string orphans
 * every job already carrying the old one, which would then be treated as
 * foreign text and pushed down the field on the next update.
 */
export const CER_BLOCK_START = '--- CER inspection details (updated automatically) ---';
export const CER_BLOCK_END = '--- end CER inspection details ---';
export const Sm8Uuid = z.string().uuid();
/**
 * Subset of the ServiceM8 Job object we read. Field set matches what the live
 * tenant returns for a real `/job/<uuid>.json`; everything else is allowed
 * through via `.passthrough()`.
 *
 * Combo PV+BTY jobs encode both IDs in `purchase_order_number` as
 * `"PVDxxxxxxx / BTYxxxxxxx"` — the queried JobID match is a substring test.
 */
export const Sm8JobSchema = z
    .object({
    uuid: Sm8Uuid,
    generated_job_id: z.string().optional(),
    /** Holds the PVD / BTY identifier(s). Combo jobs: `"PVDxxx / BTYxxx"`. */
    purchase_order_number: z.string().nullable().optional(),
    description: z.string().nullable().optional(),
    job_description: z.string().nullable().optional(),
    /** Single-line address. May be empty; reconstruct from geo_* fields if so. */
    job_address: z.string().nullable().optional(),
    geo_street: z.string().nullable().optional(),
    geo_city: z.string().nullable().optional(),
    geo_state: z.string().nullable().optional(),
    geo_postcode: z.string().nullable().optional(),
    geo_country: z.string().nullable().optional(),
    geo_number: z.string().nullable().optional(),
    lat: z.union([z.string(), z.number()]).nullable().optional(),
    lng: z.union([z.string(), z.number()]).nullable().optional(),
    category_uuid: z.string().nullable().optional(),
    /** UUID of the linked Company record — billing entity (often the tenderer, NOT the homeowner). */
    company_uuid: z.string().nullable().optional(),
    created_by_staff_uuid: z.string().nullable().optional(),
    date: z.string().nullable().optional(),
    edit_date: z.string().nullable().optional(),
    status: z.string().nullable().optional(),
    active: z.union([z.number(), z.string()]).optional(),
    /** Scheduled inspection end timestamp (when present). */
    job_is_scheduled_until_stamp: z.string().nullable().optional(),
    /**
     * SM8 returns this as either an array of UUIDs OR a single string of
     * comma-/space-separated UUIDs depending on tenant config; accept both.
     */
    badges: z
        .union([z.array(z.string()), z.string()])
        .nullable()
        .optional(),
})
    .passthrough();
/**
 * Subset of the ServiceM8 Company object we read. The Job's `company_uuid`
 * points at this — for tenderer-billed jobs (Leading Edge's main flow) this is
 * the billing tenderer (e.g. "Kerfoot Projects"), not the homeowner.
 */
export const Sm8CompanySchema = z
    .object({
    uuid: Sm8Uuid,
    name: z.string(),
    address: z.string().nullable().optional(),
    address_city: z.string().nullable().optional(),
    address_state: z.string().nullable().optional(),
    address_postcode: z.string().nullable().optional(),
    address_country: z.string().nullable().optional(),
    email: z.string().nullable().optional(),
    active: z.union([z.number(), z.string()]).optional(),
})
    .passthrough();
/** Staff record used only for explicit inspector-to-ServiceM8 matching. */
export const Sm8StaffSchema = z
    .object({
    uuid: Sm8Uuid,
    first: z.string().nullable().optional(),
    last: z.string().nullable().optional(),
    email: z.string().nullable().optional(),
    mobile: z.string().nullable().optional(),
    active: z.union([z.number(), z.string()]).optional(),
})
    .passthrough();
export const Sm8ScheduledActivitySchema = z
    .object({
    uuid: Sm8Uuid,
    job_uuid: z.string().uuid().nullable().optional(),
    staff_uuid: z.string().uuid().nullable().optional(),
    start_date: z.string(),
    end_date: z.string(),
    activity_was_scheduled: z.union([z.number(), z.string()]).optional(),
})
    .passthrough();
/**
 * A ServiceM8 `/availability.json` record — the object behind the Staff Leave
 * add-on. Both tenancies use it today (Leading Edge ~3.5k records, ACTNOW ~4k,
 * live-confirmed 2026-08-06).
 *
 * Field notes, from the live data rather than the docs:
 *   - `availability_type` is a small, *unreliable* vocabulary:
 *     `staff-annual-leave` | `staff-busy-time` | `business-closed` |
 *     `public-holiday`. It is routinely overloaded — ACTNOW files "Sick Leave",
 *     "Jason Office Day" and TAFE days all as `staff-annual-leave`. Treat the
 *     type as "is this person unavailable", never as the reason. The human
 *     meaning lives in the free-text `name`.
 *   - `regarding_object` is `staff` (per-person) or `vendor` (whole-of-business
 *     closures and public holidays).
 *   - Records are NOT recurring. A weekly "unavailable before 9am" rule is
 *     stored as one record per week, entered by hand.
 *   - `active` is a soft delete (0 = retracted, still returned by the API).
 *   - `source` is server-set and absent from the documented create payload —
 *     `calendar-sync` marks rows written by ServiceM8's own calendar
 *     integration. We cannot stamp it, so we must not rely on it to identify
 *     our own writes later.
 */
export const Sm8AvailabilitySchema = z
    .object({
    uuid: Sm8Uuid,
    active: z.union([z.number(), z.string()]).optional(),
    regarding_object: z.string().nullable().optional(),
    regarding_object_uuid: z.string().uuid().nullable().optional(),
    /**
     * Free text, and frequently personal — medical appointments, childcare
     * arrangements, "Sick Leave (approved)", "Wedding in Jervis Bay". Do NOT
     * surface it in any admin-facing response. Labels are derived from
     * `availability_type` instead. See `routes/availability.ts`.
     */
    name: z.string().nullable().optional(),
    availability_type: z.string().nullable().optional(),
    start_timestamp: z.string(),
    end_timestamp: z.string(),
    source: z.string().nullable().optional(),
    edit_date: z.string().nullable().optional(),
})
    .passthrough();
/**
 * Composite the SM8 mapper consumes: a job + its linked company.
 *
 * Note: was previously `{ job, formFields }`. Form-field reads aren't
 * authorised on the live tenant and the form is empty anyway, so the bundle
 * now carries the company instead — that's where the customer/billing name
 * lives.
 */
export const Sm8JobBundleSchema = z.object({
    job: Sm8JobSchema,
    company: Sm8CompanySchema,
});
/**
 * Normalise a ServiceM8 job's `badges` field to a list of UUIDs.
 *
 * The live tenant returns it as a JSON-encoded string (`'["uuid","uuid"]'`),
 * but the field is also documented as an array and comes back bare on some
 * tenants — the schema accepts both, so anything reading it has to as well.
 * Unparseable input yields an empty list rather than throwing: a badge we
 * cannot read must not be able to fail a job update.
 */
export function parseSm8Badges(badges) {
    if (!badges)
        return [];
    if (Array.isArray(badges))
        return badges.filter((value) => value.trim() !== '');
    const trimmed = badges.trim();
    if (trimmed === '')
        return [];
    if (trimmed.startsWith('[')) {
        try {
            const parsed = JSON.parse(trimmed);
            return Array.isArray(parsed)
                ? parsed.filter((value) => typeof value === 'string' && value.trim() !== '')
                : [];
        }
        catch {
            return [];
        }
    }
    // Comma- or space-separated, per the tenant-config variant.
    return trimmed.split(/[\s,]+/).filter(Boolean);
}
/**
 * Extract PVD/BTY accreditation codes from a ServiceM8 `purchase_order_number`.
 *
 * SM8 stores combo jobs as `"PVDxxxxxxx / BTYxxxxxxx"` — 551 of the tenancy's
 * 5,475 PO-bearing jobs are that shape as of 2026-08-11 — so an exact-equality
 * `$filter` on a single code cannot find them. Anything joining our jobs to
 * ServiceM8 has to parse the field rather than compare it.
 *
 * Deduped and uppercased, order preserved. Single-job records yield one code.
 */
export function parseAccreditationCodes(purchaseOrderNumber) {
    const matches = purchaseOrderNumber.match(/\b(?:PVD|BTY)\d+\b/gi);
    if (!matches)
        return [];
    const seen = new Set();
    const out = [];
    for (const match of matches) {
        const upper = match.toUpperCase();
        if (!seen.has(upper)) {
            seen.add(upper);
            out.push(upper);
        }
    }
    return out;
}
//# sourceMappingURL=servicem8.js.map